"use client"

import { useState, useEffect } from "react"
import { Sparkles, Loader2 } from "lucide-react"

interface StepBrandConfigProps {
  loading: boolean
  name: string[]
  context: string[]
  selectedBrandName: string
  selectedBrandContext: string
  setSelectedBrandName: (name: string) => void
  setSelectedBrandContext: (context: string) => void
}

export function StepBrandConfig({ loading, name, context, selectedBrandName, selectedBrandContext, setSelectedBrandName, setSelectedBrandContext }: StepBrandConfigProps) {
  const [brandNames, setBrandNames] = useState(name || [])
  const [brandContexts, setBrandContexts] = useState(context || [])
  const [brandNameIndex, setBrandNameIndex] = useState(0)
  const [brandContextIndex, setBrandContextIndex] = useState(0)

  useEffect(() => {
    if (name && name.length > 0) {
      setBrandNames(name)
      setSelectedBrandName(name[0])
      setBrandNameIndex(0)
    }
  }, [name])

  useEffect(() => {
    if (context && context.length > 0) {
      setBrandContexts(context)
      setSelectedBrandContext(context[0])
      setBrandContextIndex(0)
    }
  }, [context])

  const generateNewBrandName = () => {
    const currentNames = name && name.length > 0 ? name : brandNames
    if (currentNames.length === 0) return

    const nextIndex = (brandNameIndex + 1) % currentNames.length
    setBrandNameIndex(nextIndex)
    setSelectedBrandName(currentNames[nextIndex])
  }

  const generateNewBrandContext = () => {
    const currentContexts = context && context.length > 0 ? context : brandContexts
    if (currentContexts.length === 0) return

    const nextIndex = (brandContextIndex + 1) % currentContexts.length
    setBrandContextIndex(nextIndex)
    setSelectedBrandContext(currentContexts[nextIndex])
  }

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-medium mb-6 flex items-center">
        <Sparkles className="w-6 h-6 mr-2 text-primary-600" />
        Configuración de marca
      </h3>

      {/* Brand Name Section */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label htmlFor="brand-name" className="text-lg font-medium text-gray-900">
            Nombre de marca
          </label>
          <button
            type="button"
            onClick={generateNewBrandName}
            disabled={loading}
            className="inline-flex items-center justify-center rounded-lg px-4 h-9 py-0 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generando...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Regenerar con IA
              </>
            )}
          </button>
        </div>
        <input
          id="brand-name"
          type="text"
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
          placeholder="Ingresa el nombre de tu marca"
          value={selectedBrandName}
          disabled={loading}
          onChange={(e) => setSelectedBrandName(e.target.value)}
        />
      </div>

      {/* Brand Context Section */}
      <div className="mt-12">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <label htmlFor="brand-context" className="text-lg font-medium text-gray-900">
              Contexto de marca
            </label>
            <span className="text-gray-500 ml-2">Cuál es tu misión y qué hace única a tu marca</span>
          </div>
          <button
            type="button"
            onClick={generateNewBrandContext}
            disabled={loading}
            className="inline-flex items-center justify-center rounded-lg px-4 h-9 py-0 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Generando...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Regenerar con IA
              </>
            )}
          </button>
        </div>
        <textarea
          id="brand-context"
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 h-32 resize-none leading-[1.6]"
          placeholder="Describe tu marca: misión, valores, público objetivo y qué la hace única"
          value={selectedBrandContext}
          disabled={loading}
          onChange={(e) => setSelectedBrandContext(e.target.value)}
        />
      </div>
    </div>
  )
}
